import React, { useCallback, useRef } from "react";
import Sidebar from "../Sidebar/Sidebar";
import Navbar from "../Navbar/Navbar";
import "../Assets/Css/Table.css";
import { Column } from "primereact/column";
import { axiosConfig } from "../axiosAPI";
import Swal from "sweetalert2";
import { useAuth } from "../AuthProvider";
import { useNavigate, useSearchParams } from "react-router-dom";
import GlobalDataTable from "../components/GlobalDataTable";
import RideStatusBadge from "../components/RideStatusBadge";

function rowMatchesKeyword(row, keyword) {
  const k = keyword.toLowerCase();
  const hay = [
    row.passengerFullName,
    row.driverFullName,
    row.fare,
    row.date,
    row.status,
    row.type,
    row.dateScheduled,
    row.uuid,
  ]
    .map((x) => String(x ?? "").toLowerCase())
    .join(" ");
  return hay.includes(k);
}

function Request() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const auth = useAuth();
  const ridesTableRef = useRef(null);

  const defaultRowCount = 10;
  const rowCountOptions = [10, 20, 50, 100];
  const pageNoFromUrl = Math.max(1, parseInt(searchParams.get("pageNo") || "1", 10) || 1);
  const rowCountFromUrl = (() => {
    const n = parseInt(searchParams.get("rowCount") || String(defaultRowCount), 10) || defaultRowCount;
    return rowCountOptions.includes(n) ? n : defaultRowCount;
  })();
  const keywordFromUrl = searchParams.get("keyword") || "";
  const initialFirst = (pageNoFromUrl - 1) * rowCountFromUrl;

  const handleTableStateChange = useCallback(
    ({ pageNo, rows, keyword }) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams();
          if (pageNo > 1) next.set("pageNo", String(pageNo));
          if (rows !== defaultRowCount) next.set("rowCount", String(rows));
          if (keyword) next.set("keyword", keyword);
          if (prev.toString() === next.toString()) return prev;
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const buildRequestListPath = useCallback(() => {
    const state = ridesTableRef.current?.getState?.();
    const params = new URLSearchParams();
    const pageNo = state?.pageNo ?? pageNoFromUrl;
    const rows = state?.rows ?? rowCountFromUrl;
    const keyword = state?.keyword ?? keywordFromUrl;
    if (pageNo > 1) params.set("pageNo", String(pageNo));
    if (rows !== defaultRowCount) params.set("rowCount", String(rows));
    if (keyword) params.set("keyword", keyword);
    const qs = params.toString();
    return qs ? `/Request?${qs}` : "/Request";
  }, [pageNoFromUrl, rowCountFromUrl, keywordFromUrl]);

  const fetchRidesPage = useCallback(
    async ({ pageNo, rowCount, keyword }) => {
      const headers = {
        Authorization: "Bearer " + auth.token,
        "x-api-key": "fab6c94fc0044b53bf5e886a8fef1325",
      };
      const base = `${process.env.REACT_APP_BASE_URL}/dashboard/rides`;

      try {
        if (keyword) {
          const response = await axiosConfig.get(base, { headers });
          const all = Array.isArray(response.data.data.result) ? response.data.data.result : [];
          const filtered = all.filter((r) => rowMatchesKeyword(r, keyword));
          const start = (pageNo - 1) * rowCount;
          const slice = filtered.slice(start, start + rowCount);
          return { result: slice, totalCount: filtered.length };
        }

        const params = new URLSearchParams({
          pageNo: String(pageNo),
          rowCount: String(rowCount),
        });
        const response = await axiosConfig.get(`${base}?${params.toString()}`, { headers });
        return {
          result: response.data.data.result,
          totalCount: response.data.data.totalCount,
        };
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Oops...",
          text: "Something went wrong!",
        });
        return { result: [], totalCount: 0 };
      }
    },
    [auth.token]
  );

  const gotToNewPage = (rowData) => {
    return (
      <a
        className="edit"
        onClick={() => {
          const returnTo = buildRequestListPath();
          navigate(`/DetailsRequests/${rowData.uuid}`, {
            state: { item: rowData, returnTo },
          });
        }}
      >
        details
      </a>
    );
  };

  const renderPassengerName = (rowData) => {
    const name = rowData.passengerFullName;
    if (!rowData.passengerUuid || !name) {
      return name || "—";
    }
    return (
      <a
        className="edit"
        href={`/PassengerDetails/${rowData.passengerUuid}`}
        onClick={(e) => {
          e.preventDefault();
          navigate(`/PassengerDetails/${rowData.passengerUuid}`, {
            state: { returnTo: buildRequestListPath() },
          });
        }}
      >
        {name}
      </a>
    );
  };

  const renderDriverName = (rowData) => {
    const name = rowData.driverFullName;
    if (!rowData.driverUuid || !name) {
      return name || "—";
    }
    return (
      <a
        className="edit"
        href={`/Details/${rowData.driverUuid}`}
        onClick={(e) => {
          e.preventDefault();
          navigate(`/Details/${rowData.driverUuid}`, {
            state: { returnTo: buildRequestListPath() },
          });
        }}
      >
        {name}
      </a>
    );
  };

  const renderScheduledDate = (rowData) => {
    return rowData.dateScheduled ? rowData.dateScheduled : "Now";
  };

  const renderStatus = (rowData) => {
    return <RideStatusBadge status={rowData.status} />;
  };

  return (
    <>
      <div className="main">
        <div className="sidebar">
          <Sidebar />
        </div>
        <div className="dev-table">
          <Navbar />
          <div className="table-card">
            <div className="table">
              <GlobalDataTable
                ref={ridesTableRef}
                fetchPage={fetchRidesPage}
                defaultRowCount={defaultRowCount}
                rowCountOptions={rowCountOptions}
                initialFirst={initialFirst}
                initialRows={rowCountFromUrl}
                initialSearch={keywordFromUrl}
                onTableStateChange={handleTableStateChange}
                searchPlaceholder="Search passenger, driver, fare, date, status…"
                emptyMessage="No rides found."
                headerTitle={
                  <span>
                    <i className="fa fa-road me-2"></i>
                    Ride requests
                  </span>
                }
                dataKey="uuid"
                showGridlines
                tableStyle={{ minWidth: "700px" }}
              >
                <Column field="type" header="type" style={{ width: "9%" }}></Column>
                <Column field="passengerFullName" body={renderPassengerName} header="passenger" style={{ width: "12.5%" }}></Column>
                <Column field="driverFullName" body={renderDriverName} header="dirver" style={{ width: "12.5%" }}></Column>
                <Column field="fare" header="ride cost" style={{ width: "12.5%" }}></Column>
                <Column field="date" header="date" style={{ width: "12.5%" }}></Column>
                <Column field="dateScheduled" body={renderScheduledDate} header="Scheduled Date" style={{ width: "12.5%" }}></Column>
                <Column field="uuid" body={gotToNewPage} header="Details" style={{ width: "12.5%" }}></Column>
                <Column field="status" body={renderStatus} header="status" style={{ width: "12.5%" }}></Column>
              </GlobalDataTable>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Request;
